import cn from 'classnames'
import React, { ReactElement, useContext, useEffect } from 'react'

import { ConfirmRequest as ReservationConfirmRequest } from '@api/reservation'
import PageWrapper from '@components/PageWrapper'
import RequiredResource from '@components/RequiredResource'
import bem from '@lib/bem'
import reservationUtils from '@lib/reservation'
import { useCheckoutConnectionsLoader } from '@loaders/checkoutConnection'
import { useReservationConfirm } from '@loaders/confirmReservation'
import AmendmentForm from '@pages/Checkout/Amendment'
import CheckoutForm from '@pages/Checkout/Form'
import SubmitBooking from '@pages/Checkout/Form/SubmitBooking'
import SubmitReservation from '@pages/Checkout/Form/SubmitReservation'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import { BackNavigation } from '@pages/Checkout/Navigation/BackNavigation'
import { PluginContext } from '@plugins/index'
import PluginSlot from '@plugins/Slot'
import { useSettings } from '@queries/settings'
import { useCheckout } from '@stores/checkout'
import { ParamsStore, useParams } from '@stores/params'

import '@pages/Checkout/index.scss'

const App = (): ReactElement => {
  const [params] = useParams()
  const [{ reservation, quickReservation, multistepCheckout }] = useSettings()
  const [, setCheckout] = useCheckout()
  const { callbacks } = useContext(PluginContext)
  const reservationEnabled = reservation.enabled || quickReservation.enabled
  const isCurrencyChangeAllowed = !reservation.enabled
  const connectionLoader = useCheckoutConnectionsLoader(params, {
    onSuccess: data => {
      setCheckout(data)
    },
  })

  useEffect(() => {
    setCheckout({ step: Number(multistepCheckout.enabled) })
  }, [multistepCheckout.enabled, setCheckout])

  const buildConfirmParams = (
    data: CheckoutFormData,
    params: ParamsStore,
    outboundConnection: Connection | null,
  ): ReservationConfirmRequest => {
    const confirmParams = reservationUtils.buildConfirmParams(data, params, outboundConnection)
    const extraParams = callbacks?.checkout?.getReservationConfirmParams?.(data) || {}
    return { ...confirmParams, ...extraParams }
  }

  return (
    <RequiredResource
      loader={connectionLoader}
      isValidData={() => !!params.departureLocation && !!params.arrivalLocation}
    >
      <PageWrapper className="checkout-wrapper" currencyChangeAllowed={isCurrencyChangeAllowed}>
        <div className="checkout">
          <div id="bg-line" className={cn({ dense: params.express })} />
          <div id="page" className="column mb-2">
            <PluginSlot path="checkout.stepper" />
            <PluginSlot path="checkout.backNavigation" FallbackComponent={BackNavigation} />
            <div className="cell">
              <div className={bem('checkout', 'content')}>
                {reservationEnabled && !params.bookingId && (
                  <SubmitReservation
                    form={CheckoutForm}
                    submitHook={useReservationConfirm}
                    transformParams={buildConfirmParams}
                  />
                )}
                {!reservationEnabled && !params.bookingId && <SubmitBooking />}
                {params.bookingId && <AmendmentForm isLoading={connectionLoader.isLoading} />}
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>
    </RequiredResource>
  )
}

export default App
